import { motion } from "motion/react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { Zap, Users, Flame, Heart, Star, Activity, Clock, TrendingUp } from "lucide-react";

const emotions = [
  {
    name: "Happy",
    emoji: "😊",
    color: "from-yellow-400 to-orange-400",
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    description: "Share your joy",
  },
  {
    name: "Sad",
    emoji: "😢",
    color: "from-blue-400 to-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    description: "We're here for you",
  },
  {
    name: "Anxious",
    emoji: "😰",
    color: "from-indigo-400 to-cyan-500",
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    description: "Ease your worries",
  },
  {
    name: "Stressed",
    emoji: "⚡",
    color: "from-purple-400 to-purple-600",
    bg: "bg-purple-50",
    border: "border-purple-200",
    description: "Find relief",
  },
  {
    name: "Lonely",
    emoji: "🥺",
    color: "from-pink-400 to-pink-600",
    bg: "bg-pink-50",
    border: "border-pink-200",
    description: "You're not alone",
  },
  {
    name: "Angry",
    emoji: "😡",
    color: "from-red-400 to-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
    description: "Let it out safely",
  },
  {
    name: "Overwhelmed",
    emoji: "😴",
    color: "from-slate-400 to-purple-500",
    bg: "bg-slate-50",
    border: "border-slate-200",
    description: "Take a breath with us",
  },
  {
    name: "Heartbroken",
    emoji: "💔",
    color: "from-rose-400 to-red-600",
    bg: "bg-rose-50",
    border: "border-rose-200",
    description: "Heal at your own pace",
  },
];

const listenerStats = [
  {
    name: "Lives Impacted",
    value: "142",
    icon: Heart,
    color: "from-red-400 to-pink-500",
    bg: "bg-red-50",
    border: "border-red-200",
    description: "People you've helped Find peace",
  },
  {
    name: "FeelPoints",
    value: "2,450",
    icon: Zap,
    color: "from-yellow-400 to-orange-500",
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    description: "Earned through empathy",
  },
  {
    name: "Empathy Score",
    value: "9.8/10",
    icon: Star,
    color: "from-purple-400 to-purple-600",
    bg: "bg-purple-50",
    border: "border-purple-200",
    description: "Top 5% of listeners",
  },
  {
    name: "Recent Session",
    value: "45 min",
    icon: Clock,
    color: "from-blue-400 to-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    description: "Completed today at 3 PM",
  },
  {
    name: "Total Earnings",
    value: "$1,245",
    icon: TrendingUp,
    color: "from-green-400 to-emerald-600",
    bg: "bg-green-50",
    border: "border-green-200",
    description: "Keep making a difference",
  },
];

export default function EmotionCards() {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  if (user?.role === "listener") {
    return (
      <section className="py-20 px-6 bg-gradient-to-b from-white to-purple-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Your Impact as a Listener
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Every conversation you have helps someone find their path to peace
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {listenerStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className={`p-6 ${stat.bg} ${stat.border} border-2 rounded-3xl shadow-sm hover:shadow-lg transition-all group pointer-events-none`}
                >
                  <div
                    className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-current/10`}
                  >
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-black text-gray-900 mb-1">{stat.value}</p>
                    <h3 className="font-semibold text-gray-900 mb-1">{stat.name}</h3>
                    <p className="text-sm text-gray-500">{stat.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-16 text-center"
          >
            <button
              onClick={() => navigate("/listener-dashboard")}
              className="px-10 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-bold hover:shadow-xl transition-all"
            >
              Go to Dashboard
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-white to-purple-50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            How Are You Feeling?
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Select your emotion to connect with a listener who understands
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {emotions.map((emotion, index) => (
              <motion.button
                key={emotion.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                whileHover={{ y: -8, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/select-emotion")}
                className={`p-6 ${emotion.bg} ${emotion.border} border-2 rounded-3xl shadow-md hover:shadow-xl transition-shadow duration-300 group cursor-pointer`}
              >
                <div
                  className={`w-16 h-16 bg-gradient-to-br ${emotion.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-current/10`}
                >
                  <span className="text-3xl" role="img" aria-label={emotion.name}>
                    {emotion.emoji}
                  </span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  {emotion.name}
                </h3>
                <p className="text-sm text-gray-600">{emotion.description}</p>
              </motion.button>
            ))}
        </div>
      </div>
    </section>
  );
}
